import React from "react";
import { useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { Plus, Trash, UserPlus } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MovieCastNestedType } from "@/pages/movies/schemas/movie.nested.form";
import { usePeopleRepository } from "@/pages/people/hooks/use-people-repository";

type PersonOption = {
  id: string;
  name: string;
  knownForDepartment?: string;
  media?: { photos?: Array<{ url?: string; blurhash?: string }> };
};

const DEPARTMENTS = [
  "Acting",
  "Directing",
  "Writing",
  "Production",
  "Sound",
  "Camera",
];

/**
 * Cast & crew editor. Each entry is either *linked* to a Person (chosen via the
 * people search) or an *unknown* free-text name — matching the backend's
 * exactly-one-of rule. Character name and department are editable per row, and
 * rows reorder via drag (cast order is meaningful for display).
 */
export function MovieCastField({ id, inputProps }: AutoFormFieldProps) {
  const { search } = usePeopleRepository();
  const value: MovieCastNestedType[] = useWatch({ name: id }) ?? [];

  const setValue = (next: MovieCastNestedType[]) => {
    inputProps.onChange({
      target: { name: id, value: next },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const patch = (index: number, partial: Partial<MovieCastNestedType>) => {
    const next = value.map((c, i) => (i === index ? { ...c, ...partial } : c));
    setValue(next);
  };

  const addLinked = (person: PersonOption | undefined) => {
    if (!person?.id) return;
    setValue([
      ...value,
      {
        personId: person.id,
        name: person.name,
        characterName: null,
        department: person.knownForDepartment || "Acting",
        photo: person.media?.photos?.[0]?.url
          ? {
              url: person.media.photos[0].url!,
              blurhash: person.media.photos[0].blurhash ?? "",
            }
          : null,
      },
    ]);
  };

  const addUnknown = () =>
    setValue([
      ...value,
      { personId: null, name: "", characterName: null, department: "Acting" },
    ]);

  return (
    <div className="flex w-full flex-col space-y-3 text-foreground">
      <RemoteSelect<PersonOption>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={addLinked}
        accessorFn={(option) => ({
          id: option?.id || "",
          name: option?.name ?? "",
          iconUrl: option?.media?.photos?.[0]?.url,
          number: option?.knownForDepartment,
        })}
        onSearch={async (query, onStatusChanged) => {
          onStatusChanged("loading");
          const result = (await search(query)) as PersonOption[];
          onStatusChanged("success");
          return result;
        }}
      />

      <SortableList
        handle
        orientation="vertical"
        items={value.map((c, i) => ({
          ...c,
          id: c.personId ?? `unknown-${i}-${c.name ?? ""}`,
        }))}
        render={(item, index) => {
          const linked = !!item.personId;
          return (
            <div className="flex w-full flex-row items-center gap-2">
              {item.photo?.url ? (
                <PhotoWithBlurhash
                  className="size-10 shrink-0 rounded-full object-cover"
                  url={item.photo.url}
                  blurhash={item.photo.blurhash ?? ""}
                />
              ) : (
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <UserPlus className="size-4" />
                </div>
              )}

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {linked ? (
                  <span className="flex items-center gap-2 truncate text-sm font-medium">
                    {item.name}
                    <Badge variant="secondary" className="text-[10px]">
                      linked
                    </Badge>
                  </span>
                ) : (
                  <Input
                    className="h-8"
                    placeholder="Name (unknown person)"
                    defaultValue={item.name ?? ""}
                    onBlur={(e) => patch(index!, { name: e.target.value })}
                  />
                )}
              </div>

              <div className="w-40 shrink-0">
                <Input
                  className="h-8"
                  placeholder="Character"
                  defaultValue={item.characterName ?? ""}
                  onBlur={(e) =>
                    patch(index!, { characterName: e.target.value || null })
                  }
                />
              </div>

              <div className="w-32 shrink-0">
                <Input
                  className="h-8"
                  list="movie-cast-departments"
                  placeholder="Department"
                  defaultValue={item.department ?? "Acting"}
                  onBlur={(e) =>
                    patch(index!, { department: e.target.value || "Acting" })
                  }
                />
              </div>

              <Button
                variant="secondary"
                size="icon"
                type="button"
                aria-label="Remove cast member"
                onClick={() => setValue(value.filter((_, i) => i !== index))}
              >
                <Trash className="size-4" />
              </Button>
            </div>
          );
        }}
        onChanged={(items) =>
          setValue(items.map(({ id: _id, ...rest }: any) => rest))
        }
      />

      <datalist id="movie-cast-departments">
        {DEPARTMENTS.map((d) => (
          <option key={d} value={d} />
        ))}
      </datalist>

      <Button
        type="button"
        variant="secondary"
        className="flex w-fit items-center"
        onClick={addUnknown}
      >
        <Plus className="mr-2" size={16} />
        Add unknown member
      </Button>
    </div>
  );
}
